import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useDevice } from '../../store';
import { socket } from '../../lib/socket';
import { euro } from '../../lib/format';
import { useToasts } from '../../lib/toast';
import { useLang } from '../../lib/lang';
import { Keyboard } from './Keyboard';
import { Qr, ticketQrData, LOYALTY_PREFIX, parseLoyaltyQr } from '../../lib/Qr';
import { computeOrderTotals } from '@taj/domain';
import type {
  CategoryDto,
  OptionDto,
  OptionGroupDto,
  OrderDto,
  OrderType,
  PaymentMethod,
  ProductDto,
  RestaurantInfo,
  LoyaltyMember,
  DeliveryQuoteResult,
  CustomerPrefill,
  PartnerRow,
  TerminalPaymentDto,
} from '@taj/types';
import { AddressAutocomplete } from '../backoffice/DeliveryManager';

// Coordonnées par défaut (modifiables dans le back-office → chargées depuis le serveur)
const RESTO: RestaurantInfo = {
  name: 'TAJ FUSION',
  tagline: 'THAÏ · INDIEN · NAAN FUSION',
  address: '151 RUE DE VESLE · 51100 REIMS',
  phone: 'Tél. 03 26 40 93 05 · RCS 937 646 396 00013',
  web: 'WWW.TAJFUSION.COM',
};
const PAY_LABEL: Record<PaymentMethod, string> = {
  CASH: '💵 Espèces',
  TICKET_RESTO: '🎟️ Ticket papier',
  CARD: '💳 Carte Bleue & Carte ticket',
  CARTE_TICKET: '💳 Carte Bleue & Carte ticket',
  MEAL_VOUCHER: '🍽️ Titre-resto',
  ONLINE: '🏦 En ligne',
};
const pad = (n: number) => String(n).padStart(2, '0');
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
/** Résume les 7 horaires (index 0 = Lundi, '' = fermé) en lignes compactes :
 *  ['7j/7 : …'] si tous identiques, sinon regroupé par plage (['Lun-Ven : …', 'Sam-Dim : …']). */
const hoursSummary = (hours?: string[]): string[] => {
  if (!hours || hours.length < 7) return [];
  const h = hours.map((x) => (x || '').trim());
  if (h.every(Boolean) && h.every((x) => x === h[0])) return [`7j/7 : ${h[0]}`];
  const out: string[] = [];
  let i = 0;
  while (i < 7) {
    if (!h[i]) { i++; continue; }
    let j = i;
    while (j + 1 < 7 && h[j + 1] === h[i]) j++;
    out.push(`${i === j ? DAYS_FR[i] : `${DAYS_FR[i]}-${DAYS_FR[j]}`} : ${h[i]}`);
    i = j + 1;
  }
  return out;
};

interface CartLine {
  uid: string;
  product: ProductDto;
  qty: number;
  options: OptionDto[];
  notes?: string; // mot pour la cuisine (« sans oignons », « bien épicé »…)
}

// Demandes les plus courantes, proposées en un clic sur la borne (écran tactile sans clavier).
// Le client peut aussi écrire librement.
const NOTE_SUGGESTIONS: { fr: string; en: string }[] = [
  { fr: 'Sans oignons', en: 'No onions' },
  { fr: 'Sans ail', en: 'No garlic' },
  { fr: 'Sans coriandre', en: 'No coriander' },
  { fr: 'Peu épicé', en: 'Mild' },
  { fr: 'Bien épicé', en: 'Extra spicy' },
  { fr: 'Sans piment', en: 'No chilli' },
  { fr: 'Sauce à part', en: 'Sauce on the side' },
];
const NOTE_SEP = ' · ';
const NOTE_MAX = 90; // le ticket cuisine est étroit (32 colonnes) — on garde la note lisible

const badgeClass = (b: string) => (/vég|veg/i.test(b) ? 'veg' : 'pop');

// 14 allergènes officiels (UE)
const ALLERGENS = [
  { n: 'Gluten', i: '🌾' }, { n: 'Crustacés', i: '🦐' }, { n: 'Œufs', i: '🥚' }, { n: 'Poisson', i: '🐟' },
  { n: 'Arachides', i: '🥜' }, { n: 'Soja', i: '🌱' }, { n: 'Lait', i: '🥛' }, { n: 'Fruits à coque', i: '🌰' },
  { n: 'Céleri', i: '🥬' }, { n: 'Moutarde', i: '🟡' }, { n: 'Sésame', i: '⚪' }, { n: 'Sulfites', i: '🍷' },
  { n: 'Lupin', i: '🌸' }, { n: 'Mollusques', i: '🐚' },
];
const norm = (s: string) => s.trim().toLowerCase().replace(/s$/, '');
const uniq = (a: string[]) => [...new Set(a)];

const optionIcon = (n: string): string => {
  const s = n.toLowerCase();
  if (s.includes('fromage')) return '🧀';
  if (s.includes('paneer')) return '🧈';
  if (s.includes('œuf') || s.includes('oeuf')) return '🥚';
  if (s.includes('naan')) return '🫓';
  if (s.includes('cacahu') || s.includes('arachi')) return '🥜';
  if (s.includes('menthe') || s.includes('chutney')) return '🌿';
  if (s.includes('tamarin')) return '🟤';
  if (s.includes('makhani')) return '🥘';
  if (s.includes('chili') || s.includes('piment') || s.includes('fort') || s.includes('thaï')) return '🌶️';
  return '🔸';
};
const isSupplement = (g: OptionGroupDto) => /supplément|supplement/i.test(g.name);

export function Kiosk({ mode = 'kiosk', onExit, delivery = false, startType, editOrder }: { mode?: 'kiosk' | 'cashier'; onExit?: () => void; delivery?: boolean; startType?: OrderType; editOrder?: OrderDto } = {}) {
  const push = useToasts((s) => s.push);
  const borneStation = useDevice((s) => s.station); // "Borne 1" / "Borne 2" selon le lien #borne-1 / #borne-2
  const [menu, setMenu] = useState<CategoryDto[]>([]);
  const [stage, setStage] = useState<'welcome' | 'app'>(delivery || startType || editOrder ? 'app' : 'welcome');
  const [orderType, setOrderType] = useState<OrderType | null>(editOrder ? editOrder.type : delivery ? 'DELIVERY' : startType ?? null);
  // Livraison (mode caisse) : téléphone + adresse + frais
  const [dPhone, setDPhone] = useState('');
  const [dName, setDName] = useState('');
  const [dAddr, setDAddr] = useState<{ label: string; lat: number; lon: number } | null>(null);
  const [dQuote, setDQuote] = useState<DeliveryQuoteResult | null>(null);
  // Livraison manuelle (caisse) : adresse libre + prix imposé
  const [manualDelivery, setManualDelivery] = useState(false);
  const [manualAddr, setManualAddr] = useState('');
  const [manualFee, setManualFee] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const [clientName, setClientName] = useState('');
  const [activeField, setActiveField] = useState<'name' | 'phone' | null>(null);
  const [tab, setTab] = useState<'menu' | 'cart'>('menu');
  const [activeCat, setActiveCat] = useState<string>('ALL');
  const [menuSearch, setMenuSearch] = useState('');
  const [cashTab, setCashTab] = useState<'commande' | 'encaissement'>('commande'); // onglets caisse (mode POS)
  const [posDiscType, setPosDiscType] = useState<'PERCENT' | 'AMOUNT'>('PERCENT'); // remise caisse
  const [posDiscVal, setPosDiscVal] = useState(0); // valeur remise : % ou centimes
  const [posDiscInput, setPosDiscInput] = useState(''); // saisie brute
  const [posKb, setPosKb] = useState<'name' | 'phone' | null>(null); // clavier tactile caisse (nom = lettres, tél = chiffres)
  const [allergenOpen, setAllergenOpen] = useState(false);
  const [allergens, setAllergens] = useState<string[]>([]);
  const [hideRisky, setHideRisky] = useState(false);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [pay, setPay] = useState<PaymentMethod>('CASH');
  const [placing, setPlacing] = useState(false);
  // ── Paiement par carte sur le terminal (SumUp) ──
  // Le poste demande le paiement, le montant s'affiche sur le lecteur posé à côté.
  const payStation = mode === 'cashier' ? 'Caisse' : borneStation ?? 'Borne 1';
  const [terminalOk, setTerminalOk] = useState(false); // un lecteur est rattaché à ce poste
  const [termPay, setTermPay] = useState<TerminalPaymentDto | null>(null); // paiement en cours / issue
  // fiche plat
  const [detail, setDetail] = useState<ProductDto | null>(null);
  const [detailSel, setDetailSel] = useState<string[]>([]);
  const [detailQty, setDetailQty] = useState(1);
  // confirmation
  const [placedOrder, setPlacedOrder] = useState<OrderDto | null>(null);
  const [placedMethod, setPlacedMethod] = useState<PaymentMethod>('CASH');
  const [resto, setResto] = useState<RestaurantInfo>(RESTO);
  // fidélité
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [loyaltyPhone, setLoyaltyPhone] = useState('');
  const [loyaltyEmail, setLoyaltyEmail] = useState(''); // → reçoit sa carte + QR par email
  const [loyaltyDone, setLoyaltyDone] = useState<LoyaltyMember | null>(null);
  // carte fidélité utilisée sur CETTE commande (reconnaissance avant paiement → remise + points)
  const [cardInput, setCardInput] = useState('');
  const [cardMember, setCardMember] = useState<LoyaltyMember | null>(null);
  const [cardPct, setCardPct] = useState(10);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(true); // programme fidélité actif (config back-office)
  const [partnerList, setPartnerList] = useState<PartnerRow[]>([]); // partenaires (Uber Eats…) pour la caisse
  const [partnerId, setPartnerId] = useState(''); // partenaire choisi pour la commande en cours
  const [cardChecking, setCardChecking] = useState(false);

  const t = (fr: string, en: string) => (lang === 'fr' ? fr : en);
  const dishName = (p: ProductDto) => (lang === 'en' && p.nameEn ? p.nameEn : p.name);
  const dishDesc = (p: ProductDto) => (lang === 'en' && p.descriptionEn ? p.descriptionEn : p.description);
  const catName = (c: CategoryDto) => (lang === 'en' && c.nameEn ? c.nameEn : c.name);

  useEffect(() => {
    const loadMenu = () => api.getMenu().then(setMenu).catch((e) => push(e.message, 'warn'));
    const loadLoyalty = () => api.getLoyaltyConfig().then((c) => setLoyaltyEnabled(c.enabled)).catch(() => {});
    loadMenu();
    loadLoyalty();
    if (mode === 'cashier') api.listPartners().then(setPartnerList).catch(() => {});
    api.getSettings().then(setResto).catch(() => {});
    // Mise à jour en direct quand l'admin modifie la carte / les réglages.
    const onCat = () => {
      loadMenu();
      loadLoyalty();
      api.getSettings().then(setResto).catch(() => {});
    };
    socket.on('catalog:update', onCat);
    return () => {
      socket.off('catalog:update', onCat);
    };
  }, [push]);

  // Borne : si « Sur place » est désactivé en back-office, on ne propose que « À emporter ».
  useEffect(() => {
    if (mode === 'kiosk' && resto.kioskDineIn === false && (orderType === null || orderType === 'DINE_IN')) {
      setOrderType('TAKEAWAY');
    }
  }, [resto.kioskDineIn, mode, orderType]);

  // Modification d'une commande : on précharge le panier avec ses articles.
  useEffect(() => {
    if (!editOrder || !menu.length) return;
    const allProducts = menu.flatMap((c) => c.products);
    const built = editOrder.lines
      .map((l) => {
        const product = allProducts.find((p) => p.id === l.productId);
        if (!product) return null;
        const allOpts = product.optionGroups.flatMap((g) => g.options);
        const options = l.options.map((lo) => allOpts.find((o) => o.id === lo.optionId)).filter(Boolean) as OptionDto[];
        // On récupère la note écrite à la commande pour ne pas la perdre en modifiant.
        return { uid: crypto.randomUUID(), product, qty: l.qty, options, notes: l.notes ?? undefined } as CartLine;
      })
      .filter(Boolean) as CartLine[];
    setCart(built);
    if (editOrder.clientName) setDName(editOrder.clientName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOrder, menu]);

  const products = useMemo(() => {
    if (activeCat === 'ALL') return menu.flatMap((c) => c.products);
    return menu.find((c) => c.id === activeCat)?.products ?? [];
  }, [menu, activeCat]);

  const isRisky = (p: ProductDto) =>
    allergens.length > 0 && p.allergens.some((a) => allergens.some((sel) => norm(sel) === norm(a)));
  // Recherche de plats par nom (toutes catégories confondues).
  const q = menuSearch.trim().toLowerCase();
  const searched = q
    ? menu.flatMap((c) => c.products).filter((p) => dishName(p).toLowerCase().includes(q) || (dishDesc(p) ?? '').toLowerCase().includes(q))
    : products;
  const shown = hideRisky ? searched.filter((p) => !isRisky(p)) : searched;

  // ── panier ──
  const lineUnit = (l: CartLine) =>
    l.product.priceCents + l.options.reduce((s, o) => s + o.priceDelta, 0);
  const count = cart.reduce((s, l) => s + l.qty, 0);
  // Remise manuelle en caisse (POS) — prioritaire sur la remise fidélité si les deux sont là.
  const posDiscount = mode === 'cashier' && posDiscVal > 0 ? { type: posDiscType, value: posDiscVal } : undefined;
  const totals = computeOrderTotals(
    cart.map((l) => ({
      unitPriceCents: l.product.priceCents,
      qty: l.qty,
      vatRate: l.product.vatRate,
      options: l.options.map((o) => ({ priceDelta: o.priceDelta })),
    })),
    posDiscount ?? (cardMember ? { type: 'PERCENT', value: cardPct } : undefined),
  );
  const loyaltyCut = totals.subtotalCents - totals.totalCents;

  /** Reconnaît une carte : saisie manuelle (tél / code) ou QR scanné (TAJFID:TAJ1234). */
  const applyCard = async (raw?: string) => {
    const key = parseLoyaltyQr(raw ?? cardInput) || (raw ?? cardInput).trim();
    if (!key) return;
    setCardChecking(true);
    try {
      const res = await api.lookupLoyalty(key);
      setCardPct(res.discountPct);
      if (res.member) {
        setCardMember(res.member);
        push(`🎁 ${t('Carte reconnue', 'Card recognized')} · ${res.member.name ?? ''} · -${res.discountPct}% · ${res.member.points} pts`);
      } else {
        setCardMember(null);
        push(t('Carte inconnue — vérifiez le numéro ou rejoignez le programme', 'Card not found — check the number or join the program'), 'warn');
      }
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setCardChecking(false); }
  };
  const checkCard = () => applyCard();
  const clearCard = () => { setCardMember(null); setCardInput(''); };

  // ── Livraison : type changeable en direct (Sur place / Emporter / Livraison) ──
  const isDelivery = orderType === 'DELIVERY';
  const manualFeeCents = Math.round((parseFloat(manualFee.replace(',', '.')) || 0) * 100);
  const deliveryFee = !isDelivery
    ? 0
    : manualDelivery
      ? manualFeeCents
      : dQuote && dQuote.eligible
        ? (dQuote.freeAboveCents != null && totals.totalCents >= dQuote.freeAboveCents ? 0 : dQuote.feeCents)
        : 0;
  const grandTotal = totals.totalCents + deliveryFee;

  const lookupDelivery = async () => {
    if (!dPhone.trim()) return;
    try {
      const c = await api.deliveryCustomer(dPhone.trim());
      if (c) {
        setDName(c.name ?? '');
        if (c.address && c.lat != null && c.lon != null) {
          setDAddr({ label: c.address, lat: c.lat, lon: c.lon });
          setDQuote(await api.deliveryQuote(c.lat, c.lon));
        }
        push(`✅ ${t('Client trouvé', 'Customer found')} : ${c.name ?? c.phone}`);
      } else {
        push(t('Nouveau client — saisis l’adresse', 'New customer — enter address'));
      }
    } catch (e: any) { push(e.message, 'warn'); }
  };
  const pickDeliveryAddr = async (s: { label: string; lat: number; lon: number }) => {
    setDAddr(s);
    try { setDQuote(await api.deliveryQuote(s.lat, s.lon)); } catch (e: any) { push(e.message, 'warn'); }
  };

  // ── Recherche client par NOM ou TÉLÉPHONE (autocomplétion sous le champ nom) ──
  const [custSug, setCustSug] = useState<CustomerPrefill[]>([]);
  const [custOpen, setCustOpen] = useState(false);
  const skipCust = useRef(false);
  useEffect(() => {
    if (mode !== 'cashier') return;
    if (skipCust.current) { skipCust.current = false; return; }
    const key = dName.trim();
    if (key.length < 2) { setCustSug([]); setCustOpen(false); return; }
    const id = setTimeout(async () => {
      try { const list = await api.searchCustomers(key); setCustSug(list); setCustOpen(list.length > 0); } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(id);
  }, [dName, mode]);
  const pickCustomer = async (c: CustomerPrefill) => {
    skipCust.current = true;
    setDName(c.name ?? '');
    setDPhone(c.phone ?? '');
    setCustOpen(false);
    // On récupère aussi l'adresse mémorisée (prête pour la livraison) — quel que soit le type choisi.
    if (c.address && c.lat != null && c.lon != null) {
      setDAddr({ label: c.address, lat: c.lat, lon: c.lon });
      try { setDQuote(await api.deliveryQuote(c.lat, c.lon)); } catch { /* ignore */ }
    }
    push(`✅ ${t('Client', 'Customer')} : ${c.name ?? c.phone}`);
  };

  const addToCart = (product: ProductDto, options: OptionDto[], qty: number) => {
    setCart((c) => [...c, { uid: crypto.randomUUID(), product, qty, options }]);
    push(`${product.emoji ?? ''} ${dishName(product)} ${t('ajouté', 'added')}`);
  };
  const updLine = (uid: string, d: number) =>
    setCart((c) => c.map((l) => (l.uid === uid ? { ...l, qty: l.qty + d } : l)).filter((l) => l.qty > 0));
  const removeLine = (uid: string) => setCart((c) => c.filter((l) => l.uid !== uid));

  // ── Note pour la cuisine, plat par plat ──
  // Sur la borne il n'y a pas de clavier physique : on propose les demandes courantes en un clic,
  // et le clavier tactile pour le reste.
  const [noteFor, setNoteFor] = useState<string | null>(null); // uid de la ligne en cours d'annotation
  const noteLine = cart.find((l) => l.uid === noteFor) ?? null;
  const setNote = (uid: string, notes: string) =>
    setCart((c) => c.map((l) => (l.uid === uid ? { ...l, notes: notes.slice(0, NOTE_MAX) || undefined } : l)));
  /** Une suggestion se pose et se retire d'un simple appui. */
  const toggleSuggestion = (uid: string, label: string) => {
    const cur = cart.find((l) => l.uid === uid)?.notes ?? '';
    const parts = cur.split(NOTE_SEP).map((s) => s.trim()).filter(Boolean);
    const i = parts.findIndex((p) => p.toLowerCase() === label.toLowerCase());
    if (i >= 0) parts.splice(i, 1);
    else parts.push(label);
    setNote(uid, parts.join(NOTE_SEP));
  };
  const hasSuggestion = (l: CartLine, label: string) =>
    (l.notes ?? '').split(NOTE_SEP).some((p) => p.trim().toLowerCase() === label.toLowerCase());

  // ── fiche plat ──
  const openProduct = (p: ProductDto) => {
    if (p.optionGroups.length === 0) {
      addToCart(p, [], 1);
      return;
    }
    setDetail(p);
    setDetailSel([]);
    setDetailQty(1);
  };
  const toggleOpt = (opt: OptionDto, group: OptionGroupDto) => {
    const ids = group.options.map((o) => o.id);
    const inGroup = detailSel.filter((x) => ids.includes(x));
    // Déjà sélectionné → on le retire
    if (detailSel.includes(opt.id)) {
      setDetailSel(detailSel.filter((x) => x !== opt.id));
      return;
    }
    // Choix unique → remplace l'option du groupe
    if (group.maxSelect === 1) {
      setDetailSel([...detailSel.filter((x) => !ids.includes(x)), opt.id]);
      return;
    }
    // Limite atteinte → bloqué (on respecte le maximum autorisé par l'admin)
    if (group.maxSelect > 0 && inGroup.length >= group.maxSelect) {
      push(`${t('Maximum', 'Max')} ${group.maxSelect} · ${group.name}`, 'warn');
      return;
    }
    setDetailSel([...detailSel, opt.id]);
  };
  const selectedOpts = detail
    ? detail.optionGroups.flatMap((g) => g.options).filter((o) => detailSel.includes(o.id))
    : [];
  const detailUnit = (detail?.priceCents ?? 0) + selectedOpts.reduce((s, o) => s + o.priceDelta, 0);
  const detailAllergens = detail
    ? uniq([...detail.allergens, ...selectedOpts.flatMap((o) => o.allergens)])
    : [];

  const place = async (toKitchen = false) => {
    if (!cart.length || !orderType) return;
    if (isDelivery && !editOrder) {
      if (manualDelivery) {
        if (!manualAddr.trim()) { push(t('Ajoute l’adresse de livraison', 'Add delivery address'), 'warn'); return; }
      } else {
        if (!dAddr) { push(t('Ajoute l’adresse de livraison', 'Add delivery address'), 'warn'); return; }
        if (dQuote && !dQuote.eligible) { push(t('Adresse hors zone de livraison', 'Address out of delivery range'), 'warn'); return; }
      }
    }
    setPlacing(true);
    try {
      // Modification d'une commande existante (avant paiement)
      if (editOrder) {
        const upd = await api.updateOrderLines(editOrder.id, cart.map((l) => ({ productId: l.product.id, qty: l.qty, optionIds: l.options.map((o) => o.id), notes: l.notes })));
        push(`✅ ${t('Commande modifiée', 'Order updated')} · ${upd.token}`);
        setCart([]);
        onExit?.();
        return;
      }
      // Carte + terminal rattaché à ce poste → la commande attend l'accord du lecteur.
      const viaTerminal = pay === 'CARD' && terminalOk && !toKitchen;
      const order = await api.createOrder({
        channel: mode === 'cashier' ? 'CASHIER' : 'KIOSK',
        type: orderType,
        clientName: mode === 'cashier' ? (dName || clientName) : clientName,
        paymentMethod: pay,
        ...(viaTerminal ? { awaitTerminal: true } : {}),
        loyaltyPhone: cardMember?.phone,
        ...(posDiscount ? { discountType: posDiscount.type, discountValue: posDiscount.value } : {}),
        ...(toKitchen ? { sendToKitchen: true } : {}),
        ...(mode === 'cashier' && dPhone.trim() ? { customerPhone: dPhone.trim() } : {}),
        ...(partnerId ? { partnerId } : {}),
        ...(isDelivery ? (manualDelivery
          ? { deliveryPhone: dPhone, deliveryAddress: manualAddr.trim(), deliveryFeeCentsOverride: manualFeeCents }
          : (dAddr ? { deliveryPhone: dPhone, deliveryAddress: dAddr.label, deliveryLat: dAddr.lat, deliveryLon: dAddr.lon } : {})) : {}),
        lines: cart.map((l) => ({ productId: l.product.id, qty: l.qty, optionIds: l.options.map((o) => o.id), notes: l.notes })),
      });
      // Paiement sur le terminal : on bascule sur l'écran d'attente (le montant part sur le lecteur).
      if (viaTerminal) {
        setCart([]);
        try {
          setTermPay(await api.startTerminalPayment(order.id, payStation));
        } catch (e: any) {
          // Terminal injoignable : la commande reste à régler en caisse plutôt que d'être perdue.
          setTermPay({ id: '', orderId: order.id, station: payStation, status: 'FAILED', amountCents: order.totalCents, message: e.message });
        }
        return;
      }
      // Mode caisse : pas d'écran borne — on revient à la caisse, la commande apparaît dans la liste.
      if (mode === 'cashier') {
        // Commande déjà payée (carte) → on imprime le ticket client avec QR (les commandes en
        // espèces impriment leur ticket à l'encaissement via onFullyPaid).
        if (order.paidCents >= order.totalCents) {
          api.printReceipt(order.id, 'Caisse').catch(() => {});
        }
        push(`✅ ${t('Commande', 'Order')} ${order.token} ${t('créée', 'created')} → ${t('caisse', 'till')}`);
        setCart([]);
        onExit?.();
        return;
      }
      setPlacedMethod(pay);
      setPlacedOrder(order);
      setCart([]);
      setTab('menu');
    } catch (e: any) {
      push(e.message, 'warn');
    } finally {
      setPlacing(false);
    }
  };

  const doJoinLoyalty = async () => {
    if (!placedOrder) return;
    try {
      const res = await api.joinLoyalty({
        phone: loyaltyPhone,
        name: placedOrder.clientName ?? undefined,
        email: loyaltyEmail.trim() || undefined, // → reçoit sa carte + QR par email
        orderId: placedOrder.id,
      });
      setLoyaltyDone(res);
    } catch (e: any) {
      push(e.message, 'warn');
    }
  };

  // ── Scanner de carte fidélité (borne + caisse) ──
  // Un lecteur posé sur le socle agit comme un clavier : il « tape » le contenu du QR (TAJFID:TAJ1234)
  // très vite puis envoie Entrée. Le client présente le QR reçu par email sur son téléphone : sa carte
  // est reconnue sans rien toucher, la remise et les points s'appliquent.
  const checkCardRef = useRef<(code: string) => void>(() => {});
  checkCardRef.current = (code: string) => { setCardInput(code); void applyCard(code); };
  useEffect(() => {
    let buf = '';
    let last = 0;
    let flush: ReturnType<typeof setTimeout> | undefined;
    const onKey = (e: KeyboardEvent) => {
      const now = Date.now();
      if (now - last > 120) buf = ''; // grand écart entre 2 touches = frappe humaine, pas un scan
      last = now;
      if (flush) clearTimeout(flush);
      if (e.key === 'Enter') {
        if (buf.startsWith(LOYALTY_PREFIX)) { e.preventDefault(); const c = buf; buf = ''; checkCardRef.current(c); }
        else buf = '';
        return;
      }
      if (e.key.length === 1) {
        buf += e.key;
        // Repli si le lecteur n'envoie pas Entrée.
        if (buf.startsWith(LOYALTY_PREFIX)) {
          flush = setTimeout(() => { const c = buf; buf = ''; if (c.startsWith(LOYALTY_PREFIX)) checkCardRef.current(c); }, 200);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); if (flush) clearTimeout(flush); };
  }, []);

  const newOrder = () => {
    setLoyaltyOpen(false);
    setLoyaltyDone(null);
    setLoyaltyPhone('');
    setPlacedOrder(null);
    setStage('welcome');
    setOrderType(null);
    setName('');
    setPhone('');
    setClientName('');
    setTab('menu');
    setActiveCat('ALL');
    setAllergens([]);
    setHideRisky(false);
    setCardInput('');
    setCardMember(null);
  };

  // ── Terminal de paiement (SumUp) ──
  // Le poste sait-il encaisser par carte sur un lecteur ? (sinon « Carte » reste un encaissement manuel)
  useEffect(() => {
    let alive = true;
    api.terminalAvailability(payStation)
      .then((a) => { if (alive) setTerminalOk(a.available); })
      .catch(() => { if (alive) setTerminalOk(false); });
    return () => { alive = false; };
  }, [payStation]);

  // Tant que le client n'a pas présenté sa carte, on interroge le terminal toutes les 2 s.
  useEffect(() => {
    if (!termPay || termPay.status !== 'PENDING' || !termPay.id) return;
    let alive = true;
    const timer = setInterval(async () => {
      try {
        const next = await api.terminalPaymentStatus(termPay.id);
        if (!alive) return;
        setTermPay(next);
        if (next.status === 'PAID') {
          clearInterval(timer);
          if (mode === 'cashier') {
            // Caisse : la commande est réglée, on rend la main au caissier.
            push(`✅ ${t('Paiement accepté', 'Payment accepted')} · ${(next.amountCents / 100).toFixed(2)} €`);
            setTermPay(null);
            onExit?.();
          } else if (next.order) {
            // Borne : écran de confirmation habituel (ticket, fidélité, QR de suivi).
            setPlacedMethod('CARD');
            setPlacedOrder(next.order);
            setTermPay(null);
            setTab('menu');
          }
        }
      } catch { /* réseau : on réessaiera au prochain tour */ }
    }, 2000);
    return () => { alive = false; clearInterval(timer); };
  }, [termPay?.id, termPay?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Le client renonce → on arrête le terminal. La commande reste réglable en caisse. */
  const cancelTerminal = async () => {
    if (!termPay) return;
    try {
      if (termPay.id) await api.cancelTerminalPayment(termPay.id);
      setTermPay({ ...termPay, status: 'CANCELLED', message: t('Paiement annulé', 'Payment cancelled') });
    } catch (e: any) { push(e.message, 'warn'); }
  };

  /** Nouvelle tentative sur la même commande (carte refusée, mauvaise manipulation…). */
  const retryTerminal = async () => {
    if (!termPay) return;
    try {
      setTermPay(await api.startTerminalPayment(termPay.orderId, payStation));
    } catch (e: any) {
      setTermPay({ ...termPay, status: 'FAILED', message: e.message });
    }
  };

  /** Abandon du paiement carte : la commande part telle quelle, à régler à la caisse. */
  const terminalToCashier = async () => {
    if (!termPay) return;
    try {
      const order = await api.getOrder(termPay.orderId);
      setTermPay(null);
      if (mode === 'cashier') { onExit?.(); return; }
      setPlacedMethod('CASH');
      setPlacedOrder(order);
      setTab('menu');
    } catch (e: any) { push(e.message, 'warn'); }
  };

  // Borne : impression automatique du ticket dès que la commande est passée (best-effort).
  useEffect(() => {
    if (!placedOrder || mode === 'cashier') return;
    api.printReceipt(placedOrder.id, borneStation ?? undefined).catch(() => {});
  }, [placedOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Borne : retour automatique à l'accueil après 2 min (suspendu si le module fidélité est ouvert).
  useEffect(() => {
    if (!placedOrder || mode === 'cashier' || loyaltyOpen) return;
    const timer = setTimeout(() => newOrder(), 120000);
    return () => clearTimeout(timer);
  }, [placedOrder, loyaltyOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── NOTE POUR LA CUISINE (un plat à la fois) ──
  // Écran dédié : sur une borne tactile, un petit champ dans le panier serait inutilisable.
  if (noteLine) {
    const done = () => setNoteFor(null);
    return (
      <div className="nt">
        <div className="nt-card">
          <div className="nt-head">
            <div>
              <div className="nt-title">📝 {t('Un mot pour la cuisine', 'A word for the kitchen')}</div>
              <div className="nt-dish">{dishName(noteLine.product)}</div>
            </div>
            <button className="nt-x" onClick={done}>✕</button>
          </div>

          <div className="nt-preview">
            {noteLine.notes
              ? <span className="nt-txt">{noteLine.notes}</span>
              : <span className="nt-empty">{t('Aucune demande particulière', 'No special request')}</span>}
          </div>

          <div className="nt-chips">
            {NOTE_SUGGESTIONS.map((s) => {
              const label = t(s.fr, s.en);
              const on = hasSuggestion(noteLine, label);
              return (
                <button
                  key={s.fr}
                  className={`nt-chip${on ? ' on' : ''}`}
                  onClick={() => toggleSuggestion(noteLine.uid, label)}
                >
                  {on ? '✓ ' : '+ '}{label}
                </button>
              );
            })}
          </div>

          {noteLine.notes && (
            <button className="nt-clear" onClick={() => setNote(noteLine.uid, '')}>
              🧹 {t('Tout effacer', 'Clear all')}
            </button>
          )}
          <button className="nt-ok" onClick={done}>✓ {t('Valider', 'Done')}</button>
        </div>

        {/* Clavier tactile : indispensable sur la borne, qui n'a pas de clavier physique. */}
        <Keyboard
          mode="text"
          value={noteLine.notes ?? ''}
          label={t('Écrire une demande', 'Write a request')}
          placeholder={t('Ex. sans oignons', 'E.g. no onions')}
          onKey={(k) => setNote(noteLine.uid, (noteLine.notes ?? '') + k)}
          onBackspace={() => setNote(noteLine.uid, (noteLine.notes ?? '').slice(0, -1))}
          onClose={done}
        />
      </div>
    );
  }

  // ── PAIEMENT SUR LE TERMINAL (SumUp) ──
  // Plein écran : le client regarde le lecteur, pas la borne. On n'affiche que l'essentiel.
  if (termPay) {
    const waiting = termPay.status === 'PENDING';
    const amount = (termPay.amountCents / 100).toFixed(2);
    return (
      <div className="tp">
        <div className="tp-card">
          {waiting ? (
            <>
              <div className="tp-icon">💳</div>
              <div className="tp-title">{t('Paiement en cours', 'Payment in progress')}</div>
              <div className="tp-amount">{amount} €</div>
              <div className="tp-sub">
                {t('Suivez les instructions sur le terminal de paiement', 'Follow the instructions on the card terminal')}
              </div>
              <div className="tp-dots"><span /><span /><span /></div>
              <div className="tp-hint">
                {t('Insérez, approchez ou glissez votre carte.', 'Insert, tap or swipe your card.')}
                <br />{t('Carte bancaire et carte titres-restaurant acceptées.', 'Bank cards and meal-voucher cards accepted.')}
              </div>
              <button className="tp-cancel" onClick={cancelTerminal}>✕ {t('Annuler le paiement', 'Cancel payment')}</button>
            </>
          ) : (
            <>
              <div className="tp-icon tp-bad">✕</div>
              <div className="tp-title">
                {termPay.status === 'CANCELLED' ? t('Paiement annulé', 'Payment cancelled') : t('Paiement refusé', 'Payment declined')}
              </div>
              {termPay.message && <div className="tp-sub">{termPay.message}</div>}
              <div className="tp-amount">{amount} €</div>
              <div className="tp-hint">
                {t('Votre commande est conservée. Vous pouvez réessayer ou régler à la caisse.',
                   'Your order is saved. You can try again or pay at the till.')}
              </div>
              <button className="tp-retry" onClick={retryTerminal}>🔄 {t('Réessayer', 'Try again')}</button>
              <button className="tp-cancel" onClick={terminalToCashier}>
                💵 {t('Régler à la caisse', 'Pay at the till')}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── CONFIRMATION (ticket) ──
  if (placedOrder) {
    const o = placedOrder;
    const htCents = o.totalCents - o.taxCents;
    const unpaid = o.status === 'PENDING_PAYMENT';
    const lineTotal = (l: OrderDto['lines'][number]) =>
      (l.unitPriceCents + l.options.reduce((s, op) => s + op.priceDelta, 0)) * l.qty;
    return (
      <div className="cf">
        <div className="cf-sent">✓ {t('Commande envoyée !', 'Order sent!')}</div>
        <div className="cf-merci">{t('Merci', 'Thanks')}, <b>{o.clientName}</b> !</div>

        <div className="ticket">
          <div className="tk-bar" />
          <div className="tk-c">
            <div className="tk-h1">{resto.name}</div>
            <div className="tk-sub">
              {resto.tagline}<br />{resto.address}<br />{resto.phone}<br />{resto.web}
            </div>
          </div>
          <div className="tk-title">{t('TICKET DE COMMANDE', 'ORDER TICKET')}</div>
          <div className="tk-line" />
          <div className="tk-row"><span>{fmtDate(o.createdAt)}</span><span>{fmtTime(o.createdAt)}</span></div>
          <div className="tk-row"><span>{t('Client', 'Customer')}</span><span>{o.clientName}</span></div>
          <div className="tk-row">
            <span>{o.type === 'DINE_IN' ? t('Sur place', 'Dine in') : t('À emporter', 'Takeaway')}</span>
            <span>{PAY_LABEL[placedMethod]}</span>
          </div>
          <div className="tk-line" />
          {o.lines.map((l) => (
            <div key={l.id}>
              <div className="tk-item" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>{l.qty}× {l.nameSnap}</span>
                <span>{euro(lineTotal(l))}</span>
              </div>
              {l.options.length > 0 && (
                <div style={{ fontSize: '.6rem', color: '#888' }}>+ {l.options.map((op) => op.nameSnap).join(', ')}</div>
              )}
            </div>
          ))}
          <div className="tk-line" />
          <div className="tk-row"><span>{t('Sous-total HT', 'Subtotal excl. VAT')}</span><span>{euro(htCents)}</span></div>
          <div className="tk-row"><span>TVA 10 %</span><span>{euro(o.taxCents)}</span></div>
          <div className="tk-row b"><span>{t('TTC', 'Total incl. VAT')}</span><span>{euro(o.totalCents)}</span></div>
          {unpaid ? (
            <div className="tk-unpaid">⚠️ {t('NON PAYÉ — À RÉGLER', 'UNPAID — TO PAY')}<br />{euro(o.totalCents)}</div>
          ) : (
            <div className="tk-paid">✓ {t('PAYÉ', 'PAID')} · {euro(o.totalCents)}</div>
          )}
          <div className="tk-line" />
          <div className="tk-numlbl">{t('N° DE COMMANDE', 'ORDER N°')}</div>
          <div className="tk-num">{o.token ?? `#${o.number}`}</div>
          <div className="tk-line" />
          <Qr data={o.trackUrl ?? ticketQrData(o.id)} size={150} />
          <div className="tk-numlbl" style={{ marginTop: 4 }}>
            {o.trackUrl ? t('Scannez pour suivre votre commande', 'Scan to track your order') : t('Scannable en caisse', 'Scan at the till')}
          </div>
          <div className="tk-line" />
          {unpaid ? (
            <div className="tk-note">
              → {t('Avancez vers la caisse pour régler', 'Go to the till to pay')}
              <small>{t('Présentez ce numéro à la caisse', 'Show this number at the till')}</small>
            </div>
          ) : (
            <div className="tk-note">
              → {t('Votre commande est en préparation', 'Your order is being prepared')}
              <small>{t('Récupérez-la à l’appel de votre numéro', 'Collect it when your number is called')}</small>
            </div>
          )}
          <div className="tk-foot">{t('Merci de votre visite, à bientôt !', 'Thank you, see you soon!')}</div>
          <div className="tk-trace">{t('N° ticket', 'Ticket n°')} : {o.token ?? `#${o.number}`}</div>
          <div className="tk-trace">{t('Imprimé le', 'Printed on')} {fmtDate(new Date().toISOString())} - {fmtTime(new Date().toISOString())}</div>
          {hoursSummary(resto.hours).length > 0 && (
            <>
              <div className="tk-trace" style={{ marginTop: 4 }}>{t('Horaires', 'Opening hours')}</div>
              {hoursSummary(resto.hours).map((line, idx) => (
                <div className="tk-trace" key={idx}>{line}</div>
              ))}
            </>
          )}
          <div className="tk-cert">{t('Logiciel conforme NF525 — attestation éditeur ecommx solutions', 'NF525-compliant software — publisher attestation, ecommx solutions')}</div>
          {o.fiscalRef && <div className="tk-trace">{t('Trace NF525', 'NF525 trace')} : {o.fiscalRef}</div>}
        </div>

        <div className="cf-loyalty" style={{ display: loyaltyEnabled ? undefined : 'none' }}>
          <div className="lt">🎁 {t('Programme fidélité', 'Loyalty program')}</div>
          <div className="ld">{t('Recevez nos offres et -10% sur votre prochaine commande', 'Get our offers and -10% on your next order')}</div>
          <button className="lb" onClick={() => { setLoyaltyPhone(''); setLoyaltyDone(null); setLoyaltyOpen(true); }}>
            ⭐ {t('Rejoindre', 'Join')}
          </button>
        </div>

        <div className="cf-btns">
          <button
            className="cf-reprint"
            onClick={async () => {
              push(t('🖨️ Impression du ticket…', '🖨️ Printing ticket…'));
              try {
                await api.printReceipt(o.id, borneStation ?? undefined);
                push(t('✅ Ticket imprimé', '✅ Ticket printed'));
              } catch (e: any) {
                push(t('❌ Imprimante injoignable', '❌ Printer unreachable') + ` : ${e.message}`, 'warn');
              }
            }}
          >
            🖨️ {t('Imprimer le ticket', 'Print ticket')}
          </button>
          <button className="cf-new" onClick={newOrder}>✓ {t('Terminer', 'Done')}</button>
        </div>

        <div className="kl-device">⊙ Borne de commande</div>

        {loyaltyOpen && (
          <div className="al-overlay" style={{ alignItems: 'flex-start', paddingTop: '6vh' }} onClick={() => loyaltyDone && setLoyaltyOpen(false)}>
            <div className="loy-card" onClick={(e) => e.stopPropagation()}>
              {!loyaltyDone ? (
                <>
                  <div className="loy-top">
                    <div className="ti">🎁 {t('Programme fidélité', 'Loyalty program')}</div>
                    <button className="al-close" onClick={() => setLoyaltyOpen(false)}>✕</button>
                  </div>
                  <div className="loy-sub">
                    {t('Entrez votre téléphone : -10% sur la prochaine commande + 1 point par €.', 'Enter your phone: -10% next order + 1 point per €.')}
                  </div>
                  {/* Email : le client reçoit sa carte avec un QR code, qu'il présentera au scanner à sa prochaine visite. */}
                  <input
                    className="inp"
                    type="email"
                    inputMode="email"
                    placeholder={t('Email (pour recevoir votre carte)', 'Email (to receive your card)')}
                    value={loyaltyEmail}
                    onChange={(e) => setLoyaltyEmail(e.target.value)}
                    style={{ marginTop: 10 }}
                  />
                  <div style={{ fontSize: '.76rem', color: 'var(--ink-faint)', marginTop: 4 }}>
                    📧 {t('Vous recevrez votre carte avec un QR code : présentez-le au scanner à votre prochaine visite.', 'You will receive your card with a QR code: show it to the scanner on your next visit.')}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.6rem' }}>🎉</div>
                  <div className="cf-merci" style={{ marginTop: 4 }}>
                    {loyaltyDone.isNew ? t('Bienvenue', 'Welcome') : t('Re-bonjour', 'Welcome back')} !
                  </div>
                  {loyaltyDone.pointsEarned > 0 && (
                    <div style={{ fontWeight: 800, color: 'var(--green)' }}>+{loyaltyDone.pointsEarned} {t('points', 'points')} 🎯</div>
                  )}
                  <div style={{ color: 'var(--ink-dim)', fontWeight: 700, marginTop: 4 }}>
                    {t('Solde', 'Balance')} : <b style={{ color: 'var(--gold)' }}>{loyaltyDone.points} {t('points', 'points')}</b>
                  </div>
                  <div style={{ marginTop: 10, fontSize: '.78rem', color: 'var(--ink-dim)', fontWeight: 700 }}>{t('Votre code -10%', 'Your -10% code')}</div>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '2rem', color: 'var(--gold)', letterSpacing: '.08em' }}>{loyaltyDone.loyaltyCode}</div>
                  {loyaltyEmail.trim() && (
                    <div style={{ fontSize: '.8rem', color: 'var(--ink-dim)', marginTop: 8 }}>
                      📧 {t('Votre carte part par email, avec le QR code à scanner ici la prochaine fois.', 'Your card is on its way by email, with the QR code to scan here next time.')}
                    </div>
                  )}
                  <button className="cf-new" style={{ marginTop: 14 }} onClick={() => setLoyaltyOpen(false)}>OK ✓</button>
                </div>
              )}
            </div>
            {!loyaltyDone && (
              <Keyboard
                mode="tel"
                value={loyaltyPhone}
                label={t('Téléphone', 'Phone')}
                placeholder="06 12 34 56 78"
                onKey={(k) => setLoyaltyPhone((p) => p + k)}
                onBackspace={() => setLoyaltyPhone((p) => p.slice(0, -1))}
                onClose={doJoinLoyalty}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // ── WELCOME ──
  if (stage === 'welcome') {
    return (
      <div className="kioskL">
        <div className="klang">
          <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
        <div className="kl-hero">
          <div className="kl-spark">✦ ✦ ✦</div>
          <div className="kl-brand">TAJ FUSION</div>
          <div className="kl-tag">Thaï · Indien · Naan Fusion</div>
          <div className="kl-q">{t('Comment souhaitez-vous commander ?', 'How would you like to order?')}</div>
          <div className="kl-types">
            {resto.kioskDineIn !== false && (
              <div className={`kl-card${orderType === 'DINE_IN' ? ' sel' : ''}`} onClick={() => setOrderType('DINE_IN')}>
                <div className="kl-card-ic dine">🍽️</div>
                <div className="kl-card-t">{t('Sur place', 'Dine in')}</div>
                <div className="kl-card-s">{t('Servi à table', 'Served at table')}</div>
              </div>
            )}
            <div className={`kl-card${orderType === 'TAKEAWAY' ? ' sel' : ''}`} onClick={() => setOrderType('TAKEAWAY')}>
              <div className="kl-card-ic take">🛍️</div>
              <div className="kl-card-t">{t('À emporter', 'Takeaway')}</div>
              <div className="kl-card-s">{t('À récupérer', 'To collect')}</div>
            </div>
          </div>
          <div className="kl-label">{t('Votre prénom', 'Your name')}</div>
          <input
            className="kl-inp"
            placeholder={t('Ex. Amir', 'E.g. Amir')}
            value={name}
            maxLength={25}
            inputMode="none"
            onFocus={() => setActiveField('name')}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="kl-inp"
            placeholder={t('Téléphone (optionnel)', 'Phone (optional)')}
            value={phone}
            inputMode="none"
            onFocus={() => setActiveField('phone')}
            onChange={(e) => setPhone(e.target.value)}
          />
          <button className="kl-go" disabled={!orderType || !name.trim()} onClick={() => { setActiveField(null); setClientName(name.trim() || 'Client'); setStage('app'); }}>
            {t('DÉMARRER', 'START')} →
          </button>
        </div>
        {!activeField && <div className="kl-device">⊙ Borne de commande</div>}

        {activeField && (
          <Keyboard
            mode={activeField === 'phone' ? 'tel' : 'text'}
            value={activeField === 'name' ? name : phone}
            label={activeField === 'name' ? t('Votre prénom', 'Your name') : t('Téléphone', 'Phone')}
            placeholder={activeField === 'name' ? t('Ex. Amir', 'E.g. Amir') : t('Ex. 06 12 34 56 78', 'E.g. 06 12 34 56 78')}
            onKey={(k) =>
              activeField === 'name'
                ? setName((n) => (n + k).slice(0, 25))
                : setPhone((p) => p + k)
            }
            onBackspace={() =>
              activeField === 'name' ? setName((n) => n.slice(0, -1)) : setPhone((p) => p.slice(0, -1))
            }
            onClose={() => setActiveField(null)}
          />
        )}
      </div>
    );
  }

  // ── APP ──
  const TYPES: [OrderType, string, string][] = [
    ['DINE_IN', '🍽️', t('Sur place', 'Dine in')],
    ['TAKEAWAY', '🥡', t('À emporter', 'Takeaway')],
    ['DELIVERY', '🛵', t('Livraison', 'Delivery')],
  ];
  // Couleurs des cartes plats (par catégorie) pour l'écran caisse
  const CARD_COLORS = ['#7a1f2b', '#0e7c7b', '#c98a1a', '#b23a48', '#3b6e4f', '#8a5a2b', '#6a4c93', '#2b6ca3'];
  const cardColor = (p: ProductDto) => {
    const i = menu.findIndex((c) => c.id === p.categoryId);
    return CARD_COLORS[(i < 0 ? 0 : i) % CARD_COLORS.length];
  };
  const stepBtn: React.CSSProperties = { width: 30, height: 30, borderRadius: 8, border: '1px solid #ddd', background: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: '1.1rem' };
  const catBtn = (on: boolean): React.CSSProperties => ({
    width: '100%', marginBottom: 6, padding: '10px 4px', borderRadius: 10, border: 'none', cursor: 'pointer',
    fontWeight: 700, background: on ? '#d99a2b' : '#fff', color: on ? '#fff' : '#666',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, fontSize: '1.4rem',
  });
  const inCartQty = (id: string) => cart.filter((l) => l.product.id === id).reduce((n, l) => n + l.qty, 0);

  // ═══════════ ÉCRAN CAISSE (POS 3 colonnes) ═══════════
  if (mode === 'cashier') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#eef1ee' }}>
        {/* En-tête : nom/tél + types + fermer */}
        <div style={{ background: '#fff', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8, borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={onExit} style={{ padding: '9px 14px', borderRadius: 10, border: '1px solid #ddd', background: '#fff', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>← {t('Retour', 'Back')}</button>
            <span style={{ fontSize: '1.2rem' }}>📋</span>
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 140, maxWidth: 340 }}>
              <input className="inp" style={{ width: '100%' }} placeholder={t('Nom du client…', 'Customer name…')} value={dName}
                onChange={(e) => setDName(e.target.value)}
                onFocus={() => { setPosKb('name'); if (custSug.length) setCustOpen(true); }} />
              {custOpen && custSug.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, zIndex: 30, maxHeight: 240, overflow: 'auto', boxShadow: '0 10px 24px rgba(0,0,0,.15)' }}>
                  {custSug.map((c, i) => (
                    <div key={i} style={{ padding: '9px 11px', cursor: 'pointer', borderBottom: '1px solid #f2f2f2' }} onClick={() => pickCustomer(c)}>
                      👤 <b>{c.name ?? '—'}</b> · 📞 {c.phone ?? '—'}{c.address ? ` · 📍 ${c.address}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {TYPES.map(([tp, ic, lb]) => (
                <button key={tp} onClick={() => setOrderType(tp)} style={{
                  padding: '9px 14px', borderRadius: 10, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
                  border: orderType === tp ? 'none' : '1px solid #ddd',
                  background: orderType === tp ? (tp === 'DELIVERY' ? '#1f7a4d' : '#d99a2b') : '#f6f6f6',
                  color: orderType === tp ? '#fff' : '#555',
                }}>{ic} {lb}</button>
              ))}
            </div>
            {partnerList.filter((p) => p.active).length > 0 && (
              <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}
                title={t('Commande d’une plateforme partenaire (Uber Eats, Deliveroo…)', 'Order from a partner platform')}
                style={{ padding: '9px 10px', borderRadius: 10, border: partnerId ? '2px solid #e0752b' : '1px solid #ddd', fontWeight: 800, background: partnerId ? '#fff0e6' : '#f6f6f6', color: partnerId ? '#c85a12' : '#555', cursor: 'pointer' }}>
                <option value="">🏠 {t('Direct', 'Direct')}</option>
                {partnerList.filter((p) => p.active).map((p) => <option key={p.id} value={p.id}>🤝 {p.type}</option>)}
              </select>
            )}
            <button onClick={onExit} style={{ marginLeft: 'auto', width: 38, height: 38, borderRadius: '50%', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: 800 }}>✕</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.1rem' }}>📞</span>
            <input className="inp" style={{ flex: '0 1 220px' }} inputMode="tel" placeholder={t('Téléphone (optionnel)', 'Phone (optional)')} value={dPhone} onChange={(e) => setDPhone(e.target.value)} onFocus={() => setPosKb('phone')} onKeyDown={(e) => e.key === 'Enter' && lookupDelivery()} />
            <button className="cs-btn" onClick={lookupDelivery}>🔎</button>
            {isDelivery && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.8rem', fontWeight: 700, color: '#555', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={manualDelivery} onChange={(e) => setManualDelivery(e.target.checked)} /> ✍️ {t('Manuel', 'Manual')}
              </label>
            )}
            {isDelivery && (manualDelivery ? (
              <>
                <input className="inp" style={{ flex: '1 1 200px', minWidth: 150 }} placeholder={t('Adresse (saisie libre)', 'Address (free text)')} value={manualAddr} onChange={(e) => setManualAddr(e.target.value)} onFocus={() => setPosKb(null)} />
                <input className="inp" style={{ width: 92 }} inputMode="decimal" placeholder={t('Prix €', 'Price €')} value={manualFee} onChange={(e) => setManualFee(e.target.value)} onFocus={() => setPosKb(null)} />
              </>
            ) : (
              <div style={{ flex: '1 1 220px', minWidth: 180 }}>
                <AddressAutocomplete value={dAddr?.label ?? ''} placeholder={t('Adresse de livraison…', 'Delivery address…')} onPick={pickDeliveryAddr} />
              </div>
            ))}
            {isDelivery && !manualDelivery && dQuote && (dQuote.eligible
              ? <span style={{ color: '#2a7', fontWeight: 700 }}>✅ {dQuote.distanceKm} km · {deliveryFee ? euro(deliveryFee) : t('Gratuit', 'Free')}</span>
              : <span style={{ color: '#c33', fontWeight: 700 }}>❌ {dQuote.reason}</span>)}
            {isDelivery && manualDelivery && <span style={{ color: '#2a7', fontWeight: 700 }}>🛵 {manualFeeCents ? euro(manualFeeCents) : t('Prix ?', 'Price ?')}</span>}
          </div>
        </div>

        {/* Onglets Commande / Encaissement */}
        <div style={{ display: 'flex', gap: 10, padding: '8px 14px', background: '#fff', borderBottom: '1px solid #eee' }}>
          <button onClick={() => setCashTab('commande')} style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', fontWeight: 800, cursor: 'pointer', background: cashTab === 'commande' ? '#d99a2b' : '#f1f1f1', color: cashTab === 'commande' ? '#fff' : '#555' }}>🍽️ {t('Commande', 'Order')}</button>
          <button onClick={() => setCashTab('encaissement')} style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', fontWeight: 800, cursor: 'pointer', background: cashTab === 'encaissement' ? '#1f9d55' : '#f1f1f1', color: cashTab === 'encaissement' ? '#fff' : '#555' }}>💳 {t('Encaissement', 'Payment')} · {euro(grandTotal)}</button>
        </div>

        {cashTab === 'commande' ? (
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {/* GAUCHE : panier */}
            <div style={{ width: '30%', minWidth: 250, background: '#fff', borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '10px 12px', fontWeight: 800, color: '#7a1f2b', letterSpacing: '.04em' }}>📋 {t('ARTICLES', 'ITEMS')} · {count}</div>
              <div style={{ flex: 1, overflow: 'auto', padding: '0 8px' }}>
                {cart.length === 0 && <div className="no-data">🛒 {t('Panier vide', 'Empty cart')}</div>}
                {cart.map((l) => (
                  <div key={l.uid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', borderBottom: '1px solid #f2f2f2' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{dishName(l.product)}</div>
                      {l.options.length > 0 && <div style={{ fontSize: '.68rem', color: '#999' }}>{l.options.map((o) => o.name).join(' · ')}</div>}
                      {/* Le caissier note ici ce que demande le client au comptoir ou au téléphone. */}
                      <button className={`kc-note${l.notes ? ' has' : ''}`} onClick={() => setNoteFor(l.uid)}>
                        📝 {l.notes ? l.notes : t('Note cuisine', 'Kitchen note')}
                      </button>
                      <div style={{ fontSize: '.72rem', color: '#999' }}>{euro(lineUnit(l))}</div>
                    </div>
                    <button onClick={() => updLine(l.uid, -1)} style={stepBtn}>−</button>
                    <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 800 }}>{l.qty}</span>
                    <button onClick={() => updLine(l.uid, 1)} style={stepBtn}>+</button>
                    <b style={{ minWidth: 62, textAlign: 'right' }}>{euro(lineUnit(l) * l.qty)}</b>
                  </div>
                ))}
              </div>
              <div style={{ padding: 12, borderTop: '1px solid #eee' }}>
                {loyaltyCut > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b8860b', fontWeight: 700 }}><span>🎁 -{cardPct}%</span><span>-{euro(loyaltyCut)}</span></div>}
                {deliveryFee > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>🛵 {t('Livraison', 'Delivery')}</span><span>{euro(deliveryFee)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 10px' }}>
                  <span style={{ fontWeight: 700, color: '#888' }}>TOTAL TTC</span>
                  <span style={{ fontSize: '1.6rem', fontWeight: 900, color: '#c60' }}>{euro(grandTotal)}</span>
                </div>
                {isDelivery ? (
                  <>
                    <button onClick={() => place(true)} disabled={placing || count === 0} style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: count === 0 ? '#bbb' : '#1f7a4d', color: '#fff', fontWeight: 900, fontSize: '1rem', cursor: 'pointer' }}>
                      👨‍🍳 {t('Envoyer en cuisine (payer à la livraison)', 'Send to kitchen (pay on delivery)')}
                    </button>
                    <button onClick={() => setCashTab('encaissement')} disabled={count === 0} style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 12, border: '2px solid #1f9d55', background: '#fff', color: '#1f9d55', fontWeight: 800, cursor: 'pointer' }}>
                      💳 {t('Payer maintenant', 'Pay now')}
                    </button>
                  </>
                ) : (
                  <button onClick={() => place()} disabled={placing || count === 0} style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: count === 0 ? '#bbb' : '#1f9d55', color: '#fff', fontWeight: 900, fontSize: '1rem', cursor: 'pointer' }}>
                    ✓ {t('VALIDER LA COMMANDE', 'VALIDATE ORDER')} · {count}
                  </button>
                )}
              </div>
            </div>
            {/* MILIEU : plats */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, padding: 10 }}>
              <input className="inp" style={{ width: '100%', marginBottom: 10 }} placeholder={t('🔍 Rechercher un plat…', '🔍 Search a dish…')} value={menuSearch} onChange={(e) => setMenuSearch(e.target.value)} />
              <div style={{ flex: 1, overflow: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, alignContent: 'start' }}>
                {shown.length === 0 && <div className="no-data">{t('Aucun plat', 'No dish')}</div>}
                {shown.map((p) => (
                  <button key={p.id} onClick={() => openProduct(p)} style={{ position: 'relative', textAlign: 'left', border: 'none', borderRadius: 14, padding: 14, minHeight: 100, cursor: 'pointer', color: '#fff', background: cardColor(p), display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {inCartQty(p.id) > 0 && <span style={{ position: 'absolute', top: 8, right: 8, background: '#fff', color: '#333', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '.8rem' }}>{inCartQty(p.id)}</span>}
                    <div style={{ fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.15 }}>{dishName(p)}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <span style={{ fontSize: '.72rem', opacity: .85 }}>🍽 {p.id.slice(-2)}</span>
                      <b>{euro(p.priceCents)}</b>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {/* DROITE : catégories */}
            <div style={{ width: '15%', minWidth: 110, background: '#fafafa', borderLeft: '1px solid #eee', overflow: 'auto', padding: 8 }}>
              <button onClick={() => setActiveCat('ALL')} style={catBtn(activeCat === 'ALL')}>🍴<span style={{ fontSize: '.7rem', fontWeight: 700 }}>{t('Tout', 'All')}</span></button>
              {menu.map((c) => (
                <button key={c.id} onClick={() => setActiveCat(c.id)} style={catBtn(activeCat === c.id)}>
                  <span>{c.emoji ?? '🍽️'}</span><span style={{ fontSize: '.7rem', fontWeight: 700 }}>{catName(c)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* ENCAISSEMENT */
          <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
            <div style={{ maxWidth: 620, margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.5rem', fontWeight: 900, marginBottom: 16 }}>
                <span>TOTAL TTC</span><span style={{ color: '#c60' }}>{euro(grandTotal)}</span>
              </div>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>{t('Mode de paiement', 'Payment method')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {([{ v: 'CASH', i: '💵', l: t('Espèces', 'Cash') }, { v: 'CARD', i: '💳', l: t('Carte Bleue & Carte ticket', 'Card & meal card') }, { v: 'TICKET_RESTO', i: '🎟️', l: t('Ticket papier', 'Paper ticket') }] as const).map((m) => (
                  <button key={m.v} onClick={() => setPay(m.v as PaymentMethod)} style={{ padding: 18, borderRadius: 12, border: pay === m.v ? '2px solid #1f9d55' : '1px solid #ddd', background: pay === m.v ? '#eafaf0' : '#fff', fontWeight: 800, cursor: 'pointer', fontSize: '1rem' }}>{m.i} {m.l}</button>
                ))}
              </div>
              {/* Remise caisse (% ou montant) */}
              <div style={{ padding: 12, borderRadius: 12, background: '#f7f1e4', border: '1px solid #e6d3a8', marginBottom: 16 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>🏷️ {t('Remise', 'Discount')}</div>
                {posDiscVal > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, fontWeight: 700, color: '#c67c14' }}>
                      -{posDiscType === 'PERCENT' ? posDiscVal + ' %' : euro(posDiscVal)} · {t('nouveau total', 'new total')} {euro(grandTotal)}
                    </span>
                    <button className="km-back" onClick={() => { setPosDiscVal(0); setPosDiscInput(''); }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button onClick={() => setPosDiscType('PERCENT')} style={{ padding: '10px 12px', borderRadius: 10, border: 'none', fontWeight: 800, cursor: 'pointer', background: posDiscType === 'PERCENT' ? '#d99a2b' : '#eee', color: posDiscType === 'PERCENT' ? '#fff' : '#666' }}>%</button>
                    <button onClick={() => setPosDiscType('AMOUNT')} style={{ padding: '10px 12px', borderRadius: 10, border: 'none', fontWeight: 800, cursor: 'pointer', background: posDiscType === 'AMOUNT' ? '#d99a2b' : '#eee', color: posDiscType === 'AMOUNT' ? '#fff' : '#666' }}>€</button>
                    <input className="inp" inputMode="decimal" placeholder={posDiscType === 'PERCENT' ? '10' : '5,00'} value={posDiscInput} onChange={(e) => setPosDiscInput(e.target.value)} style={{ flex: 1 }} />
                    <button
                      className="kc-go"
                      style={{ width: 'auto', padding: '0 16px' }}
                      onClick={() => {
                        const raw = parseFloat(posDiscInput.replace(',', '.'));
                        if (!raw || raw <= 0) { push(t('Valeur invalide', 'Invalid value'), 'warn'); return; }
                        if (posDiscType === 'PERCENT' && raw > 100) { push(t('Max 100 %', 'Max 100%'), 'warn'); return; }
                        setPosDiscVal(posDiscType === 'PERCENT' ? Math.round(raw) : Math.round(raw * 100));
                      }}
                    >{t('Appliquer', 'Apply')}</button>
                  </div>
                )}
              </div>

              <div style={{ padding: 12, borderRadius: 12, background: '#fff8e9', border: '1px solid #f0d9a8', marginBottom: 16, display: loyaltyEnabled ? undefined : 'none' }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>🎁 {t('Carte de fidélité', 'Loyalty card')}</div>
                {cardMember ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ flex: 1 }}>✅ {cardMember.name ?? cardMember.phone} · {cardMember.points} pts · <b style={{ color: '#b8860b' }}>-{cardPct}%</b></span><button className="km-back" onClick={clearCard}>✕</button></div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}><input className="inp" style={{ flex: 1 }} placeholder={t('Téléphone ou code carte', 'Phone or card code')} value={cardInput} onChange={(e) => setCardInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && checkCard()} /><button className="kc-go" style={{ width: 'auto', padding: '0 16px' }} disabled={cardChecking} onClick={checkCard}>{t('Valider', 'Apply')}</button></div>
                )}
              </div>
              <button onClick={() => place()} disabled={placing || count === 0} style={{ width: '100%', padding: 16, borderRadius: 12, border: 'none', background: count === 0 ? '#bbb' : '#1f9d55', color: '#fff', fontWeight: 900, fontSize: '1.1rem', cursor: 'pointer' }}>
                ✓ {t('ENCAISSER', 'CHARGE')} · {euro(grandTotal)}
              </button>
              {isDelivery && (
                <button onClick={() => place(true)} disabled={placing || count === 0} style={{ width: '100%', marginTop: 10, padding: 16, borderRadius: 12, border: '2px solid #d99a2b', background: '#fff8e9', color: '#8a5a2b', fontWeight: 900, fontSize: '1rem', cursor: 'pointer' }}>
                  👨‍🍳 {t('Envoyer en cuisine (paiement à la livraison)', 'Send to kitchen (pay on delivery)')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Fenêtre options d'un plat (partagée) */}
        {detail && (
          <div className="al-overlay" onClick={() => setDetail(null)}>
            <div className="al-sheet" onClick={(e) => e.stopPropagation()}>
              <div className="pd-img">
                {detail.photoUrl ? <img className="pd-img-photo" src={detail.photoUrl} alt="" /> : detail.emoji}
                <button className="pd-close" onClick={() => setDetail(null)}>✕</button>
              </div>
              <div className="pd-name">{dishName(detail)}</div>
              {dishDesc(detail) && <div className="pd-desc">{dishDesc(detail)}</div>}
              <div className="pd-body">
                {detail.optionGroups.map((g) => (
                  <div key={g.id} style={{ marginBottom: 12 }}>
                    <div className="pd-label">{g.name}{g.maxSelect > 0 && <span style={{ color: 'var(--ink-dim)', fontWeight: 600 }}> · max {g.maxSelect}</span>}</div>
                    <div className="pd-chips">
                      {g.options.map((o) => (
                        <button key={o.id} className={`pd-chip${detailSel.includes(o.id) ? ' on' : ''}`} onClick={() => toggleOpt(o, g)}>
                          {o.imageUrl && <img className="pd-chip-img" src={o.imageUrl} alt="" />}
                          {o.name}{o.priceDelta > 0 ? ` +${euro(o.priceDelta)}` : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="pd-foot">
                <div className="pd-qty">
                  <button onClick={() => setDetailQty((q) => Math.max(1, q - 1))}>−</button>
                  <span>{detailQty}</span>
                  <button onClick={() => setDetailQty((q) => q + 1)}>+</button>
                </div>
                <button className="pd-add" onClick={() => { addToCart(detail, selectedOpts, detailQty); setDetail(null); }}>
                  {t('Ajouter', 'Add')} · {euro(detailUnit * detailQty)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clavier tactile : nom = lettres seulement, téléphone = pavé numérique */}
        {posKb && (
          <Keyboard
            mode={posKb === 'phone' ? 'tel' : 'text'}
            value={posKb === 'phone' ? dPhone : dName}
            label={posKb === 'phone' ? t('Téléphone', 'Phone') : t('Nom du client', 'Customer name')}
            placeholder={posKb === 'phone' ? t('Ex. 06 12 34 56 78', 'E.g. 06 12 34 56 78') : t('Ex. Amir', 'E.g. Amir')}
            onKey={(k) => (posKb === 'phone' ? setDPhone((p) => p + k) : setDName((n) => (n + k).slice(0, 30)))}
            onBackspace={() => (posKb === 'phone' ? setDPhone((p) => p.slice(0, -1)) : setDName((n) => n.slice(0, -1)))}
            onClose={() => setPosKb(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="km">
      <div className="km-top">
        <button className="km-back" onClick={() => (tab === 'cart' ? setTab('menu') : setStage('welcome'))}>←</button>
        <div className="km-hello">
          <div className="ot">
            {tab === 'cart'
              ? `${count} ${t('articles', 'items')} · ${orderType === 'DINE_IN' ? t('Sur place', 'Dine in') : t('À emporter', 'Takeaway')}`
              : orderType === 'DINE_IN' ? `🍽️ ${t('Sur place', 'Dine in')}` : `🛍️ ${t('À emporter', 'Takeaway')}`}
          </div>
          {tab === 'cart' ? (
            <div className="nm km-title">{t('Panier', 'Cart').toUpperCase()}{count > 0 && <span className="km-cnt">{count}</span>}</div>
          ) : (
            <div className="nm">{t('Bienvenue', 'Welcome')}, {clientName}</div>
          )}
        </div>
        {tab === 'menu' && (
          <button className={`km-aller${allergens.length ? ' on' : ''}`} onClick={() => setAllergenOpen(true)}>
            ⚠️ {t('Allergènes', 'Allergens')}{allergens.length ? ` ·${allergens.length}` : ''}
          </button>
        )}
        <div className="km-lang">
          <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </div>

      {tab === 'menu' ? (
        <div className="km-body">
          <div className="km-side">
            <button className={`km-cat${activeCat === 'ALL' ? ' on' : ''}`} onClick={() => setActiveCat('ALL')}>
              <span className="ic">🍴</span><span className="lb">{t('Tout', 'All')}</span>
            </button>
            {menu.map((c) => (
              <button key={c.id} className={`km-cat${activeCat === c.id ? ' on' : ''}`} onClick={() => setActiveCat(c.id)}>
                {c.imageUrl ? <img className="km-cat-img" src={c.imageUrl} alt="" /> : <span className="ic">{c.emoji ?? '🍽️'}</span>}
                <span className="lb">{catName(c)}</span>
              </button>
            ))}
          </div>
          <div className="km-main">
            <div style={{ padding: '8px 10px 4px' }}>
              <input
                className="inp"
                style={{ width: '100%' }}
                placeholder={t('🔍 Rechercher un plat…', '🔍 Search a dish…')}
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
              />
            </div>
            <div className="km-grid">
              {shown.length === 0 && <div className="no-data">{t('Aucun plat trouvé', 'No dish found')}</div>}
              {shown.map((p) => (
                <div key={p.id} className="km-pcard" onClick={() => openProduct(p)}>
                  <div className="km-img">
                    {p.photoUrl && <img className="km-photo-bg" src={p.photoUrl} alt="" />}
                    {p.badges[0] && <span className={`km-badge ${badgeClass(p.badges[0])}`}>★ {p.badges[0]}</span>}
                    {isRisky(p) && <span className="km-risk">⚠️ {t('Allergène', 'Allergen')}</span>}
                    {!p.photoUrl && (
                      <>
                        {p.emoji}
                        <div className="km-photo">photo · {p.id.slice(-3)}</div>
                      </>
                    )}
                  </div>
                  <div className="km-pbody">
                    <div className="km-pname">{dishName(p)}</div>
                    <div className="km-pdesc">{dishDesc(p)}</div>
                    <div className="km-pfoot">
                      <span className="km-price">{euro(p.priceCents)}</span>
                      <button className="km-add" onClick={(e) => { e.stopPropagation(); openProduct(p); }}>+</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="kc-body">
          <div className="kc-scroll">
            {cart.length === 0 ? (
              <div className="no-data">🛒 {t('Panier vide', 'Empty cart')}</div>
            ) : (
              <>
                {cart.map((l) => (
                  <div key={l.uid} className="kc-item">
                    <div className="kc-emoji">{l.product.emoji}</div>
                    <div className="kc-info">
                      <div className="kc-name">{dishName(l.product)}</div>
                      {l.options.length > 0 && (
                        <div style={{ fontSize: '.7rem', color: 'var(--ink-dim)', fontWeight: 600 }}>
                          {l.options.map((o) => o.name).join(' · ')}
                        </div>
                      )}
                      {/* Mot pour la cuisine : visible dès qu'il est écrit, sinon simple invitation. */}
                      <button className={`kc-note${l.notes ? ' has' : ''}`} onClick={() => setNoteFor(l.uid)}>
                        📝 {l.notes ? l.notes : t('Ajouter un mot pour la cuisine', 'Add a word for the kitchen')}
                      </button>
                      <div className="kc-price">{euro(lineUnit(l) * l.qty)}</div>
                    </div>
                    <button className="kc-trash" onClick={() => removeLine(l.uid)}>🗑</button>
                    <div className="kc-step">
                      <button onClick={() => updLine(l.uid, -1)}>−</button>
                      <span className="q">{l.qty}</span>
                      <button onClick={() => updLine(l.uid, 1)}>+</button>
                    </div>
                  </div>
                ))}
                <div className="kc-paylabel">{t('Mode de paiement', 'Payment method')}</div>
                <div className="kc-pay-grid">
                  {([
                    { v: 'CASH', i: '💵', l: t('Espèces', 'Cash') },
                    { v: 'TICKET_RESTO', i: '🎟️', l: t('Ticket papier', 'Paper ticket') },
                    { v: 'CARD', i: '💳', l: t('Carte Bleue & Carte ticket', 'Card & meal card') },
                  ] as const).map((p) => (
                    <div key={p.v} className={`kc-pay${pay === p.v ? ' sel' : ''}`} onClick={() => setPay(p.v as PaymentMethod)}>
                      <div className="pi">{p.i}</div>
                      <div className="pl">{p.l}</div>
                    </div>
                  ))}
                </div>
                {/* Le client doit savoir où il va payer avant de valider. */}
                <div className="kc-payhint">
                  {pay === 'CARD' && terminalOk
                    ? `💳 ${t('Paiement sur le terminal, juste à côté de la borne.', 'Payment on the card terminal, next to the kiosk.')}`
                    : `🧾 ${t('À régler à la caisse en récupérant votre commande.', 'To be paid at the till when you collect your order.')}`}
                </div>

                {/* 🎁 Carte de fidélité — reconnaissance avant paiement (remise + points) */}
                <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: '#fff8e9', border: '1px solid #f0d9a8', display: loyaltyEnabled ? undefined : 'none' }}>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>🎁 {t('Carte de fidélité', 'Loyalty card')}</div>
                  {cardMember ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1 }}>✅ {cardMember.name ?? cardMember.phone} · {cardMember.points} pts · <b style={{ color: '#b8860b' }}>-{cardPct}%</b></span>
                      <button className="km-back" onClick={clearCard}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className="inp"
                        style={{ flex: 1 }}
                        placeholder={t('Téléphone ou code carte', 'Phone or card code')}
                        value={cardInput}
                        onChange={(e) => setCardInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && checkCard()}
                      />
                      <button className="kc-go" style={{ width: 'auto', padding: '0 16px' }} disabled={cardChecking} onClick={checkCard}>{t('Valider', 'Apply')}</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {cart.length > 0 && (
            <div className="kc-footer">
              <div className="kc-sub"><span>{t('Sous-total', 'Subtotal')}</span><span>{euro(totals.subtotalCents)}</span></div>
              {loyaltyCut > 0 && (
                <div className="kc-sub" style={{ color: '#b8860b', fontWeight: 700 }}>
                  <span>🎁 {t('Remise fidélité', 'Loyalty discount')} -{cardPct}%</span><span>-{euro(loyaltyCut)}</span>
                </div>
              )}
              {deliveryFee > 0 && (
                <div className="kc-sub"><span>🛵 {t('Frais de livraison', 'Delivery fee')}</span><span>{euro(deliveryFee)}</span></div>
              )}
              <div className="kc-total"><span className="lbl">TOTAL</span><span className="amt">{euro(grandTotal)}</span></div>
              <button className="kc-go" disabled={placing} onClick={() => place()}>
                {t('COMMANDER', 'ORDER')} · {euro(grandTotal)}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'menu' && <div className="km-device2">⊙ Borne de commande</div>}

      <div className="bnav">
        <button className={`bnt${tab === 'menu' ? ' active' : ''}`} onClick={() => setTab('menu')}>
          <span className="bnt-icon">🍱</span><span className="bnt-lbl">Menu</span>
        </button>
        <button className={`bnt${tab === 'cart' ? ' active' : ''}`} onClick={() => setTab('cart')}>
          <span className="bnt-icon">🛒</span>{count > 0 && <span className="bnt-badge">{count}</span>}<span className="bnt-lbl">{t('Panier', 'Cart')}</span>
        </button>
      </div>

      {/* ── Filtre allergènes ── */}
      {allergenOpen && (
        <div className="al-overlay" onClick={() => setAllergenOpen(false)}>
          <div className="al-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="al-grip" />
            <div className="al-head">
              <div className="ic">⚠️</div>
              <div>
                <div className="ti">{t('Filtre allergènes', 'Allergen filter')}</div>
                <div className="su">{t('Cochez vos allergies — les plats à risque seront signalés', 'Check your allergies — risky dishes will be flagged')}</div>
              </div>
              <button className="al-close" onClick={() => setAllergenOpen(false)}>✕</button>
            </div>
            <div className="al-grid">
              {ALLERGENS.map((a) => {
                const on = allergens.includes(a.n);
                return (
                  <div key={a.n} className={`al-item${on ? ' on' : ''}`} onClick={() => setAllergens((s) => (on ? s.filter((x) => x !== a.n) : [...s, a.n]))}>
                    <span className="ai">{a.i}</span><span className="an">{a.n}</span><span className="al-check">{on ? '✓' : ''}</span>
                  </div>
                );
              })}
            </div>
            <div className="al-toggle">
              <span className="ai">🚫</span><span className="an">{t('Masquer les plats à risque', 'Hide risky dishes')}</span>
              <div className={`al-sw${hideRisky ? ' on' : ''}`} onClick={() => setHideRisky((v) => !v)} />
            </div>
            <button className="al-go" onClick={() => setAllergenOpen(false)}>{t('VOIR LES PLATS', 'SEE DISHES')}</button>
          </div>
        </div>
      )}

      {/* ── Fiche plat / personnalisation ── */}
      {detail && (
        <div className="al-overlay" onClick={() => setDetail(null)}>
          <div className="al-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="pd-img">
              {detail.photoUrl ? <img className="pd-img-photo" src={detail.photoUrl} alt="" /> : detail.emoji}
              <button className="pd-close" onClick={() => setDetail(null)}>✕</button>
            </div>
            <div className="pd-name">{dishName(detail)}</div>
            {dishDesc(detail) && <div className="pd-desc">{dishDesc(detail)}</div>}

            <div className="pd-body">
              {detail.optionGroups.map((g) => {
                const picked = g.options.filter((o) => detailSel.includes(o.id)).length;
                const hint =
                  g.maxSelect === 1
                    ? g.minSelect >= 1 ? t('choix obligatoire', 'required') : t('1 choix', 'pick 1')
                    : `${picked}/${g.maxSelect} ${t('max', 'max')}`;
                return (
                <div key={g.id}>
                  <div className="pd-label">
                    {g.name}
                    <span style={{ color: 'var(--ink-faint)', fontWeight: 700, textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>
                      · {hint}
                    </span>
                  </div>
                  {isSupplement(g) ? (
                    g.options.map((o) => (
                      <div key={o.id} className={`pd-row${detailSel.includes(o.id) ? ' on' : ''}`} onClick={() => toggleOpt(o, g)}>
                        {o.imageUrl ? <img className="pd-thumb" src={o.imageUrl} alt="" /> : <span className="ic">{optionIcon(o.name)}</span>}
                        <span className="nm">{o.name}{o.allergens.length > 0 && <span className="pd-warn">⚠️</span>}</span>
                        <span className="pr">{o.priceDelta > 0 ? `+ ${euro(o.priceDelta)}` : ''}</span>
                      </div>
                    ))
                  ) : (
                    <div className="pd-chips">
                      {g.options.map((o) => (
                        <div key={o.id} className={`pd-chip${detailSel.includes(o.id) ? ' on' : ''}`} onClick={() => toggleOpt(o, g)}>
                          {o.imageUrl && <img className="pd-chip-img" src={o.imageUrl} alt="" />}
                          {o.name}{o.allergens.length > 0 && <span className="pd-warn">⚠️</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}

              {detailAllergens.length > 0 && (
                <div className="pd-allerbox">
                  <div className="t">⚠️ {t('Allergènes', 'Allergens')} ({detailAllergens.length})</div>
                  <div className="c">{t('Contient (options incluses)', 'Contains (options included)')} : {detailAllergens.join(' · ')}</div>
                </div>
              )}
            </div>

            <div className="pd-foot">
              <div className="pd-qty">
                <button onClick={() => setDetailQty((q) => Math.max(1, q - 1))}>−</button>
                <span className="q">{detailQty}</span>
                <button onClick={() => setDetailQty((q) => q + 1)}>+</button>
              </div>
              <button className="pd-add" onClick={() => { addToCart(detail, selectedOpts, detailQty); setDetail(null); }}>
                {t('AJOUTER AU PANIER', 'ADD TO CART')} · {euro(detailUnit * detailQty)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
